import { useEffect, useMemo, useRef, useState } from 'react';
import { nuevaClaveIntento } from '@renderer/lib/claveIntento';
import { conConfirmacionDeCarga } from '@renderer/lib/confirmarCargaRepetida';
import { useNavigate } from 'react-router-dom';
import { Sparkles, Check, Minus, Plus, X, ArrowLeft, PackagePlus, Search } from 'lucide-react';
import type { SugerenciaCargaDelDiaVista, SugerenciaCargaInsumoVista } from '@shared/types';
import { useSessionStore } from '@renderer/store/useSessionStore';
import { NumberField } from '@renderer/components/ui/NumberField';
import { HelpButton } from '@renderer/components/help/HelpButton';
import { tourPrecargar } from '@renderer/tours';
import { traducirErrorBackend } from '@renderer/lib/mensajesError';

/**
 * Sprint 25 — Wizard de precarga de inventario al iniciar el día.
 *
 * UX:
 *   - Cards compactas con solo lo indispensable: nombre, stock actual,
 *     vendido ayer, cantidad sugerida editable, ✓ Aceptar / ⊘ Saltar.
 *   - Filtro default "Solo con sugerencia > 0" para no saturar.
 *   - Botón "Aceptar todas" si la dueña confía en las sugerencias.
 *   - Botón "Cargar todo aceptado" ejecuta `cargaDiaria` con todos los
 *     aceptados en 1 transacción.
 *
 * Reutiliza el handler existente `insumos.cargaDiaria` — cero backend nuevo
 * para la persistencia.
 */

type EstadoCard = 'pendiente' | 'aceptada' | 'saltada';

interface CardState {
  cantidadActual: number;
  estado: EstadoCard;
}

export function PrecargarInventarioPage() {
  const navigate = useNavigate();
  const sesion = useSessionStore((s) => s.sesion);

  const [data, setData] = useState<SugerenciaCargaDelDiaVista | null>(null);
  const [estados, setEstados] = useState<Map<number, CardState>>(new Map());
  const [filtro, setFiltro] = useState<'sugerencia' | 'todos'>('sugerencia');
  const [busqueda, setBusqueda] = useState('');
  const [cargando, setCargando] = useState(true);
  const [enviando, setEnviando] = useState(false);
  const [mensaje, setMensaje] = useState<{ tipo: 'ok' | 'error'; texto: string } | null>(null);

  const cargar = async () => {
    setCargando(true);
    const res = await window.api.insumos.sugerenciaCargaDelDia();
    if (res.ok) {
      setData(res.data);
      // Inicializar estados con sugerencias del backend.
      const next = new Map<number, CardState>();
      for (const i of res.data.insumos) {
        next.set(i.insumoId, { cantidadActual: i.sugerencia, estado: 'pendiente' });
      }
      setEstados(next);
    }
    setCargando(false);
  };

  useEffect(() => {
    cargar();
  }, []);

  const insumosFiltrados = useMemo(() => {
    if (!data) return [];
    const q = busqueda.trim().toLowerCase();
    return data.insumos
      .filter((i) => (q ? i.nombre.toLowerCase().includes(q) : true))
      .filter((i) => (filtro === 'sugerencia' ? i.sugerencia > 0 : true))
      .sort((a, b) => {
        // Mayor sugerencia primero, después alfabético.
        if (b.sugerencia !== a.sugerencia) return b.sugerencia - a.sugerencia;
        return a.nombre.localeCompare(b.nombre);
      });
  }, [data, filtro, busqueda]);

  const counts = useMemo(() => {
    let aceptadas = 0;
    let saltadas = 0;
    let pendientesConSug = 0;
    let conSugerencia = 0;
    if (!data) return { aceptadas, saltadas, pendientesConSug, conSugerencia, total: 0 };
    for (const i of data.insumos) {
      const st = estados.get(i.insumoId);
      // Sprint 44 hotfix — `aceptadas` y `saltadas` cuentan TODOS los insumos
      // sin importar si tenían sugerencia. Caso real: cliente alpha con poco
      // historial → backend devuelve sugerencia=0 en todos los insumos. El
      // usuario carga manualmente cantidades y aprieta "✓ Aceptar" en cada
      // card, pero el botón final "Cargar todo aceptado" quedaba DISABLED
      // porque `aceptadas` ignoraba los insumos sin sugerencia. `conSugerencia`
      // y `pendientesConSug` SÍ siguen condicionados (alimentan el botón
      // "Aceptar todas las sugerencias automáticas").
      if (st?.estado === 'aceptada') aceptadas++;
      else if (st?.estado === 'saltada') saltadas++;
      if (i.sugerencia > 0) {
        conSugerencia++;
        if (!st || st.estado === 'pendiente') pendientesConSug++;
      }
    }
    return { aceptadas, saltadas, pendientesConSug, conSugerencia, total: data.insumos.length };
  }, [data, estados]);

  const setCard = (insumoId: number, patch: Partial<CardState>) => {
    setEstados((prev) => {
      const next = new Map(prev);
      const actual = next.get(insumoId) ?? { cantidadActual: 0, estado: 'pendiente' as EstadoCard };
      next.set(insumoId, { ...actual, ...patch });
      return next;
    });
  };

  const aceptarTodas = () => {
    if (!data) return;
    setEstados((prev) => {
      const next = new Map(prev);
      for (const i of data.insumos) {
        if (i.sugerencia > 0) {
          const actual = next.get(i.insumoId) ?? { cantidadActual: i.sugerencia, estado: 'pendiente' as EstadoCard };
          next.set(i.insumoId, { ...actual, estado: 'aceptada' });
        }
      }
      return next;
    });
  };

  const limpiarSeleccion = () => {
    if (!data) return;
    setEstados((prev) => {
      const next = new Map(prev);
      for (const i of data.insumos) {
        next.set(i.insumoId, { cantidadActual: i.sugerencia, estado: 'pendiente' });
      }
      return next;
    });
  };

  /*
    Sprint 165 — El 23/10 esta carga quedó registrada dos veces en el mismo segundo. El freno va al
    PRINCIPIO, antes de la pregunta "¿Cargar los insumos aceptados?": dos toques abrían dos preguntas.
  */
  const enviandoRef = useRef(false);
  const cargarTodoAceptado = async () => {
    if (!sesion || !data || enviandoRef.current) return;
    enviandoRef.current = true;
    try {
      await cargarTodoAceptadoUnaVez();
    } finally {
      enviandoRef.current = false;
    }
  };
  const cargarTodoAceptadoUnaVez = async () => {
    if (!sesion || !data) return;
    const cargas = [...estados.entries()]
      .filter(([, st]) => st.estado === 'aceptada' && st.cantidadActual > 0)
      .map(([insumoId, st]) => ({ insumoId, cargado: st.cantidadActual, mermaPct: 0 }));
    if (cargas.length === 0) {
      setMensaje({ tipo: 'error', texto: 'No hay insumos aceptados con cantidad mayor a 0.' });
      return;
    }
    const ok = await window.confirmar({
      titulo: '¿Cargar los insumos aceptados?',
      mensaje: `Se SUMARÁ al stock actual en ${cargas.length} insumo(s). Esta acción no se puede deshacer.`,
      textoConfirmar: 'Sí, cargar todo',
      variant: 'info',
    });
    if (!ok) return;
    setEnviando(true);
    setMensaje(null);
    const clave = nuevaClaveIntento();
    const res = await conConfirmacionDeCarga((repetidaConfirmada) =>
      window.api.insumos.cargaDiaria({ cargas, usuarioId: sesion.usuarioId, claveIntento: clave, repetidaConfirmada: repetidaConfirmada || undefined }),
    );
    setEnviando(false);
    if (res.ok) {
      setMensaje({
        tipo: 'ok',
        texto: `Cargados ${res.data.totalCargado.toFixed(2)} en ${cargas.length} insumos.`,
      });
      // Recargar para reflejar nuevo stock y resetear estado.
      setTimeout(() => navigate('/inventario'), 1200);
    } else {
      setMensaje({ tipo: 'error', texto: traducirErrorBackend(res.error, res.codigo) });
    }
  };

  return (
    <div className="space-y-4 max-w-6xl mx-auto">
      <header className="flex items-center gap-3" data-tour="pre-header">
        <button onClick={() => navigate('/inventario')} className="btn-ghost p-2" aria-label="Volver">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <Sparkles className="h-7 w-7 text-primary" />
        <div className="flex-1">
          <h1 className="text-2xl font-bold">Precargar inventario</h1>
          <p className="text-sm text-slate-500">
            {data?.nombreDiaObjetivo
              ? `Sugerencias para ${data.nombreDiaObjetivo} basadas en los últimos ${data.nombreDiaObjetivo}s.`
              : `Sugerencias basadas en lo vendido ayer${data ? ` (${formatearFecha(data.fechaReferencia)})` : ''}.`}
            {' '}Ajusta o salta lo que necesites.
          </p>
        </div>
        <HelpButton tour={tourPrecargar} />
      </header>

      {cargando ? (
        <div className="card text-center py-8 text-slate-400">Calculando sugerencias…</div>
      ) : !data || data.insumos.length === 0 ? (
        <div className="card text-center py-8 text-slate-500">
          <PackagePlus className="h-12 w-12 mx-auto mb-3 text-slate-300" />
          <p className="font-medium">No hay insumos en el catálogo.</p>
        </div>
      ) : (
        <>
          {/* Sprint 25 hotfix — Banner "todo en orden" como mensaje NO bloqueante.
              Antes este bloque reemplazaba al wizard y dejaba al usuario sin
              forma de ver el resto. Ahora se muestra arriba y el filtro
              "Todos" hace su trabajo correctamente. */}
          {counts.conSugerencia === 0 && filtro === 'sugerencia' && (
            <div className="rounded-xl border-2 border-emerald-300 bg-emerald-50 p-4 flex items-start gap-3">
              <Check className="h-6 w-6 text-emerald-600 shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="font-semibold text-emerald-900">
                  ¡Todo el inventario está en orden!
                </div>
                <div className="text-sm text-emerald-800 mt-0.5">
                  No hay sugerencias de carga: el stock actual cubre lo que se vendió ayer.
                  Puedes ver todos los insumos por si quieres cargar manualmente alguno.
                </div>
                <button
                  onClick={() => setFiltro('todos')}
                  className="btn-ghost text-sm mt-2 text-emerald-700"
                >
                  Ver todos los insumos →
                </button>
              </div>
            </div>
          )}

          {/* Resumen + filtros */}
          <div className="card flex flex-col gap-3 md:flex-row md:items-center md:justify-between" data-tour="pre-filtros">
            <div className="flex flex-wrap gap-2">
              <Chip
                etiqueta={`🔥 Con sugerencia · ${counts.conSugerencia}`}
                activo={filtro === 'sugerencia'}
                onClick={() => setFiltro('sugerencia')}
                color="amber"
              />
              <Chip
                etiqueta={`Todos · ${counts.total}`}
                activo={filtro === 'todos'}
                onClick={() => setFiltro('todos')}
                color="slate"
              />
            </div>
            <div className="relative md:w-72">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                type="text"
                className="input pl-9"
                placeholder="Buscar insumo…"
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
              />
            </div>
          </div>

          {/* Estado de selección + atajos */}
          <div className="card flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="text-sm">
              <span className="font-semibold text-emerald-700">{counts.aceptadas} aceptadas</span>
              <span className="mx-2 text-slate-300">·</span>
              <span className="text-slate-500">{counts.saltadas} saltadas</span>
              <span className="mx-2 text-slate-300">·</span>
              <span className="text-amber-700">{counts.pendientesConSug} sin decidir</span>
            </div>
            <div className="flex gap-2">
              <button
                onClick={aceptarTodas}
                className="btn-ghost text-sm"
                disabled={enviando || counts.pendientesConSug === 0}
              >
                <Check className="h-4 w-4" /> Aceptar todas
              </button>
              <button onClick={limpiarSeleccion} className="btn-ghost text-sm" disabled={enviando}>
                Reiniciar
              </button>
            </div>
          </div>

          {/* Grid de cards */}
          {insumosFiltrados.length === 0 ? (
            <div className="card text-center py-6 text-slate-500">
              {filtro === 'sugerencia' ? (
                <>
                  No hay insumos con sugerencia de carga.{' '}
                  <button
                    onClick={() => setFiltro('todos')}
                    className="text-primary font-medium underline"
                  >
                    Ver todos
                  </button>
                </>
              ) : (
                'No hay insumos que coincidan con tu búsqueda.'
              )}
            </div>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-tour="pre-cards">
              {insumosFiltrados.map((i) => (
                <SugerenciaCard
                  key={i.insumoId}
                  insumo={i}
                  estado={estados.get(i.insumoId) ?? { cantidadActual: i.sugerencia, estado: 'pendiente' }}
                  onCantidadChange={(n) => setCard(i.insumoId, { cantidadActual: n })}
                  onAceptar={() => setCard(i.insumoId, { estado: 'aceptada' })}
                  onSaltar={() => setCard(i.insumoId, { estado: 'saltada' })}
                  onLimpiar={() => setCard(i.insumoId, { estado: 'pendiente' })}
                />
              ))}
            </div>
          )}

          {/* Botón final sticky */}
          <div className="card sticky bottom-4 z-10 bg-slate-50 border-2 border-slate-200" data-tour="pre-final">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
              <div className="flex-1 text-sm">
                {counts.aceptadas > 0 ? (
                  <span className="text-emerald-700 font-medium">
                    Vas a cargar {counts.aceptadas} insumo(s) aceptado(s).
                  </span>
                ) : (
                  <span className="text-slate-500">Acepta al menos un insumo para cargar.</span>
                )}
              </div>
              <button
                onClick={cargarTodoAceptado}
                disabled={enviando || counts.aceptadas === 0}
                className="btn-primary btn-lg"
              >
                <PackagePlus className="h-5 w-5" />
                {enviando ? 'Cargando…' : 'Cargar todo aceptado'}
              </button>
            </div>
            {mensaje && (
              <div
                className={`mt-3 rounded-lg px-3 py-2 text-sm ${
                  mensaje.tipo === 'ok' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                }`}
              >
                {mensaje.texto}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function SugerenciaCard({
  insumo,
  estado,
  onCantidadChange,
  onAceptar,
  onSaltar,
  onLimpiar,
}: {
  insumo: SugerenciaCargaInsumoVista;
  estado: CardState;
  onCantidadChange: (n: number) => void;
  onAceptar: () => void;
  onSaltar: () => void;
  onLimpiar: () => void;
}) {
  const style =
    estado.estado === 'aceptada'
      ? 'border-emerald-400 bg-emerald-50'
      : estado.estado === 'saltada'
      ? 'border-slate-300 bg-slate-50 opacity-60'
      : 'border-slate-200 bg-white';

  return (
    <div className={`rounded-xl border-2 ${style} p-3 flex flex-col gap-2 shadow-sm transition`}>
      {/* Header con nombre + unidad + chip estado */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-slate-800 text-base leading-tight truncate">{insumo.nombre}</h3>
          <div className="text-xs uppercase tracking-wide text-slate-500">{insumo.unidad}</div>
        </div>
        {estado.estado === 'aceptada' && (
          <span className="inline-flex items-center rounded-full bg-emerald-600 text-white px-2 py-0.5 text-[10px] font-bold">
            ✓ Aceptada
          </span>
        )}
        {estado.estado === 'saltada' && (
          <span className="inline-flex items-center rounded-full bg-slate-400 text-white px-2 py-0.5 text-[10px] font-bold">
            ⊘ Saltada
          </span>
        )}
      </div>

      {/* Datos compactos */}
      <div className="grid grid-cols-2 gap-1 text-xs">
        <div>
          <div className="text-xs uppercase tracking-wide text-slate-600">Tengo</div>
          <div className="font-mono font-semibold text-slate-700">
            {insumo.stockActual} {insumo.unidad}
          </div>
        </div>
        <div>
          <div className="text-xs uppercase tracking-wide text-slate-600">Ayer vendí</div>
          <div className="font-mono font-semibold text-blue-700">
            {insumo.vendidoAyer} {insumo.unidad}
          </div>
        </div>
      </div>

      {/* Sprint 34 — Patrón histórico mismo día de la semana (si hay datos). */}
      {insumo.patronVentasMismoDia && insumo.patronVentasMismoDia.length > 0 && (
        <div className="rounded-md bg-amber-50 border border-amber-200 px-2 py-1.5">
          <div className="text-xs uppercase tracking-wide text-amber-700 font-semibold">
            Patrón {insumo.origenSugerencia === 'patron' ? 'mismo día' : ''}
          </div>
          <div className="font-mono text-[11px] text-amber-900 leading-tight">
            {insumo.patronVentasMismoDia.map((v) => v.toFixed(1)).join(' · ')}
          </div>
        </div>
      )}

      {/* Cantidad editable */}
      <div>
        <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">Cargar</div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => onCantidadChange(Math.max(0, estado.cantidadActual - 1))}
            className="w-8 h-8 rounded border border-slate-300 hover:bg-slate-100 flex items-center justify-center"
            aria-label="Disminuir"
            disabled={estado.estado === 'saltada'}
          >
            <Minus className="h-4 w-4" />
          </button>
          <NumberField
            className="input flex-1 text-center font-mono text-base h-9"
            min={0}
            decimals={3}
            value={estado.cantidadActual}
            onChange={onCantidadChange}
            disabled={estado.estado === 'saltada'}
          />
          <button
            type="button"
            onClick={() => onCantidadChange(estado.cantidadActual + 1)}
            className="w-8 h-8 rounded border border-slate-300 hover:bg-slate-100 flex items-center justify-center"
            aria-label="Aumentar"
            disabled={estado.estado === 'saltada'}
          >
            <Plus className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Acciones */}
      <div className="flex gap-1 mt-1">
        {estado.estado === 'pendiente' && (
          <>
            <button
              onClick={onAceptar}
              className="btn-primary flex-1 text-xs"
              style={{ minHeight: '36px' }}
            >
              <Check className="h-4 w-4" /> Aceptar
            </button>
            <button
              onClick={onSaltar}
              className="btn-ghost flex-1 text-xs text-slate-600"
              style={{ minHeight: '36px' }}
            >
              <X className="h-4 w-4" /> Saltar
            </button>
          </>
        )}
        {estado.estado !== 'pendiente' && (
          <button
            onClick={onLimpiar}
            className="btn-ghost flex-1 text-xs"
            style={{ minHeight: '36px' }}
          >
            Cambiar
          </button>
        )}
      </div>
    </div>
  );
}

function Chip({
  etiqueta,
  activo,
  color,
  onClick,
}: {
  etiqueta: string;
  activo: boolean;
  color: 'slate' | 'amber';
  onClick: () => void;
}) {
  const colorMap = {
    slate: 'bg-slate-700 text-white border-slate-700',
    amber: 'bg-amber-500 text-white border-amber-500',
  };
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-full px-3 py-1.5 text-sm font-medium border-2 transition ${
        activo ? colorMap[color] : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300'
      }`}
    >
      {etiqueta}
    </button>
  );
}

function formatearFecha(iso: string): string {
  // iso es 'YYYY-MM-DD'. Mostramos como "sábado 17 may".
  const d = new Date(`${iso}T12:00:00`);
  return d.toLocaleDateString('es-MX', { weekday: 'long', day: '2-digit', month: 'short' });
}
